import { byteLength, type RunResponse } from '../sandboxd';
import { parseVizStderr } from '../viz';
import { generateInput, STDIN_BYTE_LIMIT, type MeterSpec } from './generate';
import { evalOps, parseOps } from './ops';
import { TOO_SLOW_OPS } from './predict';

/** §2.1。 */
export interface MeterPoint {
  n: number;
  /** viz.tick が無ければ null。 */
  ticks: number | null;
  /** final が無く途中経過から取った値なら true（表示は「≥」）。 */
  ticksLowerBound: boolean;
  durationMs: number;
  timedOut: boolean;
  exitCode: number;
}

export type SkipReason = 'input-too-large' | 'predicted-too-slow';

export interface MeterResult {
  points: MeterPoint[];
  skipped: { n: number; reason: SkipReason }[];
}

export interface MeterPlan {
  /** 送るサイズ（昇順）。stdins と同じ順。 */
  sizes: number[];
  stdins: string[];
  skipped: { n: number; reason: SkipReason }[];
}

/**
 * 計測の計画（§2 手順 1〜3）。昇順にし、1,000,000 バイト超の入力を除き、
 * ops があれば 10^10 回を超えるサイズを間引く（その中の最小の 1 サイズだけは送る。§5.3）。
 */
export function planMeasurement(meter: MeterSpec): MeterPlan {
  const sorted = [...new Set(meter.sizes)].sort((a, b) => a - b);
  const skipped: MeterPlan['skipped'] = [];
  const kept: { n: number; stdin: string }[] = [];
  for (const n of sorted) {
    const stdin = generateInput(meter.input, n);
    if (byteLength(stdin) > STDIN_BYTE_LIMIT) skipped.push({ n, reason: 'input-too-large' });
    else kept.push({ n, stdin });
  }
  let send = kept;
  if (meter.ops !== undefined) {
    const tree = parseOps(meter.ops);
    let allowedOver = true;
    send = kept.filter(({ n }) => {
      if (evalOps(tree, { N: n }) <= TOO_SLOW_OPS) return true;
      if (allowedOver) {
        allowedOver = false;
        return true;
      }
      skipped.push({ n, reason: 'predicted-too-slow' });
      return false;
    });
  }
  return { sizes: send.map((k) => k.n), stdins: send.map((k) => k.stdin), skipped };
}

/** 入力が大きすぎて除いたときの文言（§2 手順 2）。 */
export function inputTooLargeNotice(sizes: readonly number[]): string {
  return `入力が 1MiB を超えるため N = ${sizes.join(', ')} は除きました`;
}

/**
 * 応答から計測結果を作る（§2 手順 5）。CE のときは計測結果を出さず診断だけを返す。
 * res は validateRunResponse 済みのもの（sandboxd クライアントの戻り値）を渡す。
 */
export function collectMeterResult(
  plan: MeterPlan,
  res: RunResponse,
): { ok: true; result: MeterResult } | { ok: false; compileOutput: string } {
  if (res.compile?.ok !== true) return { ok: false, compileOutput: res.compile?.output ?? '' };
  const runs = res.runs ?? [];
  const points: MeterPoint[] = plan.sizes.map((n, i) => {
    const run = runs[i];
    const trace = parseVizStderr(run?.stderr ?? '');
    return {
      n,
      ticks: trace.ticks?.total ?? null,
      ticksLowerBound: trace.ticks !== null && !trace.ticks.final,
      durationMs: run?.durationMs ?? 0,
      timedOut: run?.timedOut ?? false,
      exitCode: run?.exitCode ?? -1,
    };
  });
  return { ok: true, result: { points, skipped: plan.skipped } };
}

/** durationMs のグラフに描く最小の N（§3.2）。 */
export const CHART_MIN_N = 10_000;
/** timedOut の点を置く天井（§4）。 */
export const TIMEOUT_CEILING_MS = 3_000;

/** グラフ用の点（§3.2, §4）。N ≥ 10^4 だけを残し、timedOut は 3,000ms に置く。 */
export function chartPoints(
  points: readonly MeterPoint[],
): { n: number; ms: number; timedOut: boolean }[] {
  return points
    .filter((p) => p.n >= CHART_MIN_N)
    .map((p) => ({
      n: p.n,
      ms: p.timedOut ? TIMEOUT_CEILING_MS : p.durationMs,
      timedOut: p.timedOut,
    }));
}
